﻿import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { CSSProperties, ReactNode, RefObject, SVGProps } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
  cssTransitionFromExpandTransition,
  mergeReducedMotionPreset,
  resolveExpandTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import type { ReferenceImageItem } from '../../api/referenceImages'
import { resolveReferenceImageDisplayUrl } from '../../api/referenceUpload'
import { useBrokenImageHttpStatus } from '../../hooks/useBrokenImageHttpStatus'
import { useDeferUntilVisible } from '../../hooks/useDeferUntilVisible'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { useScrollContainerWidth } from '../../hooks/useScrollContainerWidth'
import { LIBRARY_MANAGEMENT_RAIL_WIDTH_CSS } from '../../lib/libraryLayout'
import {
  LIBRARY_MASONRY_GAP_DESKTOP_PX,
  LIBRARY_MASONRY_GAP_MOBILE_PX,
  libraryMasonryColumnCount,
  libraryMasonryLayoutCompact,
  splitLibraryMasonryColumns,
} from '../../lib/libraryMasonry'
import { masonryGridColumnsStyle } from '../../lib/masonryColumnSplit'
import {
  classifyReferenceImageLink,
  referenceImageThumbnailUrl,
  signedPrivateDownloadUrlStillValid,
} from '../../lib/referenceImageUrl'
import { type ThemeId,useTheme } from '../../theme/ThemeContext'
import { ChromePrimaryButton, ChromeToolbarButton, ChromeToolbarDangerButton } from '../ChromeActions'
import { chromeIconButtonClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { ImageLoadFailurePlaceholder } from '../ImageLoadFailurePlaceholder'
import {
  IconCloseSmall,
  IconEyeSmall,
  IconFolderPlus,
  IconFolderTrash,
  IconLibraryFolder,
  IconLibraryManage,
  IconMoveToFolder,
  IconPencil,
  IconTrash,
  IconUploadSmall,
} from '../nav-icons'
import { LibraryFolderPathBreadcrumb } from './LibraryFolderPathBreadcrumb'
import { ThemedLibraryState } from './ThemedLibraryState'

export { LibraryFolderPathBreadcrumb } from './LibraryFolderPathBreadcrumb'

function ellipsisMiddle(text: string, head = 10, tail = 10): string {
  const raw = text.trim()
  if (raw.length <= head + tail + 1) return raw
  return `${raw.slice(0, head)}...${raw.slice(raw.length - tail)}`
}

/** 图库「全屏预览」眼睛：部分主题在叠图/深色底上需单独提亮（不改图标组件） */
function libraryPreviewEyeIconClass(theme: ThemeId): string {
  switch (theme) {
    case 'geek':
      return 'text-[color-mix(in_srgb,var(--ink)_58%,var(--segment-active-fg)_42%)]'
    case 'pixel':
      return 'text-[var(--accent)]'
    case 'cartoon':
    case 'illustration':
      return 'text-[color-mix(in_srgb,var(--ink)_72%,#2c1810_28%)]'
    default:
      return ''
  }
}

/** 仅作用于卡片内 {@link ImageLoadFailurePlaceholder} 子树中的 SVG（裂图 + 刷新） */
function libraryAssetCardFailurePlaceholderSvgClass(theme: ThemeId): string {
  switch (theme) {
    case 'geek':
      return '[&_svg]:text-[color-mix(in_srgb,var(--ink)_54%,var(--line)_46%)]'
    case 'pixel':
      return '[&_svg]:text-[var(--accent)]'
    case 'cartoon':
    case 'illustration':
      return '[&_svg]:text-[color-mix(in_srgb,var(--ink)_68%,#2a1d16_32%)]'
    default:
      return ''
  }
}

/** 图库卡片：仅展示图片（外层为 Grid 分列或移动网格，不做 CSS multicol） */
function LibraryAssetCard({
  img,
  angular,
  active,
  compact,
  pcDesktopColumns,
  eagerImage,
  scrollRootRef,
  scrollRootAttachSignal,
  suppressImgNetwork,
  onSelect,
  onOpenLightbox,
  onRefreshImageUrl,
  onFatalLoadFailure,
}: {
  img: ReferenceImageItem
  angular: boolean
  active: boolean
  /** 窄屏：更小的缩略图策略与单列布局，避免按原图像素「撑满」观感 */
  compact?: boolean
  /** lg+ 瀑布列数（1｜2｜3），用于 `sizes` 与列宽一致；不传则按两列取值 */
  pcDesktopColumns?: 1 | 2 | 3
  /** 首屏若干张强制 eager，减轻多列 + lazy 在移动 WebKit 上偶发不触发加载 */
  eagerImage?: boolean
  /** 图库主滚动容器：嵌套 lazy 以此为 IntersectionObserver root */
  scrollRootRef?: RefObject<HTMLElement | null>
  scrollRootAttachSignal?: number
  /** 灯箱已打开同一资源时暂停本卡片 `<img>`，避免与灯箱并发请求同一外链 */
  suppressImgNetwork?: boolean
  onSelect: (img: ReferenceImageItem) => void
  onOpenLightbox: (img: ReferenceImageItem) => void
  onRefreshImageUrl: (img: ReferenceImageItem) => Promise<ReferenceImageItem | null>
  /** 缩略图最终无法加载（含刷新 URL 失败后）——用于父级清空当前选中项 */
  onFatalLoadFailure?: (publicId: string) => void
}) {
  const { theme } = useTheme()
  const previewEyeIconClass = libraryPreviewEyeIconClass(theme)
  const failureSvgToneClass = libraryAssetCardFailurePlaceholderSvgClass(theme)
  const deferGridImg = !!suppressImgNetwork
  const [reloadNonce, setReloadNonce] = useState(0)
  const [thumbOk, setThumbOk] = useState(true)
  const [recoveringOverlay, setRecoveringOverlay] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const urlHints = useMemo(
    () => ({ bucket: img.bucket, objectKey: img.objectKey }),
    [img.bucket, img.objectKey],
  )
  const [resolvedUrl, setResolvedUrl] = useState(() =>
    resolveReferenceImageDisplayUrl(img.url, urlHints),
  )
  const [refreshedOnce, setRefreshedOnce] = useState(false)
  const [devReferrerMode, setDevReferrerMode] = useState<'default' | 'no-referrer'>('default')
  /** 同步挡并发：error/load 可能在同一帧多次触发，避免重复 refresh-url */
  const urlRefreshInFlightRef = useRef(false)
  const normalizedUrl = resolvedUrl
  /** PC 双列栅格 + 高分屏：与列宽、`sizes` 对齐，便于等比例取更大的展示图 */
  const thumbMaxW = compact ? 480 : 2560
  const thumbUrl = useMemo(
    () => referenceImageThumbnailUrl(normalizedUrl, thumbMaxW, urlHints),
    [normalizedUrl, thumbMaxW, urlHints],
  )
  const tryThumbFirst = thumbUrl !== normalizedUrl
  const [useThumb, setUseThumb] = useState(tryThumbFirst)
  const displaySrc = useThumb ? thumbUrl : normalizedUrl
  const linkKind = useMemo(
    () => classifyReferenceImageLink(displaySrc, urlHints),
    [displaySrc, urlHints],
  )
  const referrerPolicy = useMemo(() => {
    if (!import.meta.env.DEV || linkKind !== 'remote-cdn') {
      return undefined
    }
    return devReferrerMode === 'no-referrer' ? 'no-referrer' : undefined
  }, [devReferrerMode, linkKind])
  /** 外链 CDN 跨域 fetch 探测无意义且会多 2 次失败请求，仅对 app-hosted 探测 */
  const failureHttpStatus = useBrokenImageHttpStatus(
    displaySrc,
    !thumbOk && linkKind === 'app-hosted',
  )

  const retryImageLoad = useCallback(() => {
    setReloadNonce((n) => n + 1)
    setThumbOk(true)
    setRecoveringOverlay(false)
    setRefreshedOnce(false)
    setDevReferrerMode('default')
    setImageLoaded(false)
    decodeOkRef.current = false
    urlRefreshInFlightRef.current = false
  }, [])

  const mediaAspectStyle = useMemo((): CSSProperties | undefined => {
    const w = img.width
    const h = img.height
    if (w != null && h != null && w > 0 && h > 0) {
      return { aspectRatio: `${w} / ${h}` }
    }
    return undefined
  }, [img.width, img.height])

  /** 解码成功（用于超时兜底：既不 load 也不 error 时不永久空白） */
  const decodeOkRef = useRef(false)
  const handleImgFailureRef = useRef<() => void>(() => {})

  handleImgFailureRef.current = () => {
    if (useThumb && tryThumbFirst) {
      setUseThumb(false)
      return
    }
    if (import.meta.env.DEV && linkKind === 'remote-cdn' && devReferrerMode === 'default') {
      setDevReferrerMode('no-referrer')
      setReloadNonce((n) => n + 1)
      return
    }
    if (refreshedOnce) {
      setThumbOk(false)
      setImageLoaded(true)
      return
    }
    if (signedPrivateDownloadUrlStillValid(normalizedUrl)) {
      setThumbOk(false)
      setImageLoaded(true)
      return
    }
    if (urlRefreshInFlightRef.current) return
    urlRefreshInFlightRef.current = true
    setRefreshedOnce(true)
    void onRefreshImageUrl(img)
      .then((next) => {
        if (!next?.url) {
          setThumbOk(false)
          setImageLoaded(true)
          return
        }
        const refreshed = resolveReferenceImageDisplayUrl(next.url, urlHints)
        const refreshedThumb = referenceImageThumbnailUrl(refreshed, thumbMaxW, urlHints)
        setResolvedUrl(refreshed)
        setUseThumb(refreshedThumb !== refreshed)
        setThumbOk(true)
        setImageLoaded(false)
      })
      .catch(() => {
        setThumbOk(false)
        setImageLoaded(true)
      })
      .finally(() => {
        urlRefreshInFlightRef.current = false
      })
  }

  useEffect(() => {
    setImageLoaded(false)
  }, [displaySrc])

  useEffect(() => {
    setRecoveringOverlay(false)
  }, [displaySrc])

  useLayoutEffect(() => {
    const next = resolveReferenceImageDisplayUrl(img.url, urlHints)
    setResolvedUrl(next)
    const nextThumb = referenceImageThumbnailUrl(next, thumbMaxW, urlHints)
    setUseThumb(nextThumb !== next)
    setThumbOk(true)
    setRecoveringOverlay(false)
    setRefreshedOnce(false)
    setDevReferrerMode('default')
    setImageLoaded(false)
    decodeOkRef.current = false
    urlRefreshInFlightRef.current = false
    setReloadNonce(0)
  }, [img.publicId, img.url, thumbMaxW, urlHints])

  useEffect(() => {
    decodeOkRef.current = false
  }, [displaySrc])

  useEffect(() => {
    if (!displaySrc.trim()) {
      setThumbOk(false)
      setImageLoaded(true)
    }
  }, [displaySrc])

  useEffect(() => {
    if (!thumbOk) return
    if (deferGridImg) return
    const id = window.setTimeout(() => {
      if (!decodeOkRef.current) {
        setThumbOk(false)
        setImageLoaded(true)
      }
    }, 6000)
    return () => window.clearTimeout(id)
  }, [thumbOk, displaySrc, img.publicId, deferGridImg])

  useEffect(() => {
    if (!thumbOk) onFatalLoadFailure?.(img.publicId)
  }, [thumbOk, img.publicId, onFatalLoadFailure])

  const fatalOrRecoveringBlocked = !thumbOk || recoveringOverlay
  /** 缩略图解码完成后再叠预览按钮，避免骨架阶段出现可点控件 */
  const canShowCardOverlays = imageLoaded && !recoveringOverlay && !fatalOrRecoveringBlocked
  /** 选中态描边仅在可交互时出现；加载失败或过载恢复中不可选不可预览 */
  const showSelectionRing = active && !fatalOrRecoveringBlocked

  const desktopColsEffective = compact ? 2 : (pcDesktopColumns ?? 2)
  const imgSizes = compact
    ? '(max-width: 1023px) 96vw, 280px'
    : desktopColsEffective >= 3
      ? '(max-width: 1280px) 58vw, (max-width: 1920px) 42vw, 900px'
      : '(max-width: 1280px) 84vw, (max-width: 1920px) 58vw, 1200px'

  const { wrapRef, active: inViewForLoad } = useDeferUntilVisible({
    rootRef: scrollRootRef,
    rootAttachSignal: scrollRootAttachSignal,
    defer: !eagerImage,
    rootMargin: compact ? '260px' : '360px',
  })

  /** 外层已由 aspect-ratio 占位时，省略 img 的 width/height 属性，避免裂图等小 intrinsic 宽度把 flex 卡片撑成窄条 */
  const imgLayoutAttrs =
    !compact && mediaAspectStyle ? {} : { width: img.width ?? undefined, height: img.height ?? undefined }

  const tileShellClass = [
    'relative isolate w-full min-w-0 overflow-hidden bg-surface-muted',
    mediaAspectStyle && !compact
      ? 'min-h-[10rem] w-full'
      : compact
        ? 'aspect-square min-h-[9.5rem]'
        : 'aspect-[4/5] min-h-[clamp(14rem,36vw,28rem)]',
  ].join(' ')

  return (
    <article
      className={[
        'group relative flex min-w-0 w-full max-w-full flex-shrink-0 flex-col self-stretch overflow-hidden border-line bg-panel text-ink shadow-card',
        'transition-[box-shadow,colors] duration-200 motion-reduce:transition-none',
        !thumbOk ? 'pointer-events-none' : '',
        '[@media(hover:hover)_and_(pointer:fine)]:hover:shadow-float',
        showSelectionRing
          ? angular
            ? 'z-[1] ring-2 ring-inset ring-accent shadow-float'
            : 'z-[1] ring-2 ring-inset ring-accent/45 shadow-float'
          : '',
        angular ? 'rounded-none border-2' : 'rounded-theme border',
      ].join(' ')}
    >
      {!thumbOk ? (
        <div className={[tileShellClass, 'pointer-events-auto'].join(' ')} style={mediaAspectStyle}>
          <ImageLoadFailurePlaceholder
            angular={angular}
            compact={compact}
            httpStatus={failureHttpStatus}
            onReload={retryImageLoad}
            className={[
              'absolute inset-0 z-[3] flex h-full min-h-0 w-full flex-col',
              failureSvgToneClass,
              angular ? 'rounded-none' : '',
            ].join(' ')}
          />
        </div>
      ) : (
        <div className="relative min-w-0">
          <button
            type="button"
            disabled={fatalOrRecoveringBlocked}
            className={[
              'relative block w-full min-w-0 border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50',
              fatalOrRecoveringBlocked ? 'cursor-not-allowed' : '',
            ].join(' ')}
            onClick={() => {
              if (fatalOrRecoveringBlocked) return
              onSelect(img)
            }}
            aria-label={fatalOrRecoveringBlocked ? '图片加载中或不可用' : `选择 ${img.name}`}
          >
            <div ref={wrapRef} className={tileShellClass} style={mediaAspectStyle}>
              {deferGridImg ? (
                <div
                  className="glyphicai-skeleton-shimmer glyphicai-skeleton-card-pulse absolute inset-0 z-[1] ring-1 ring-inset ring-line/45"
                  aria-hidden
                />
              ) : (
                <>
                  {!imageLoaded ? (
                    <div
                      className="glyphicai-skeleton-shimmer glyphicai-skeleton-card-pulse absolute inset-0 z-[1] ring-1 ring-inset ring-line/45"
                      aria-hidden
                    />
                  ) : null}
                  {recoveringOverlay ? (
                    <div
                      className="glyphicai-skeleton-shimmer glyphicai-skeleton-card-pulse absolute inset-0 z-[4] ring-1 ring-inset ring-line/45"
                      aria-hidden
                    />
                  ) : null}
                  {/*
                    生产：保留默认 Referer 以满足 CDN 白名单。
                    开发：localhost:5173 常不在七牛防盗链白名单 → 对 remote-cdn 用 no-referrer（需控制台允许空 Referer）。
                  */}
                  <img
                    key={`${displaySrc}-${reloadNonce}-${devReferrerMode}`}
                    src={inViewForLoad ? displaySrc : undefined}
                    alt=""
                    referrerPolicy={referrerPolicy}
                    {...imgLayoutAttrs}
                    sizes={imgSizes}
                    loading={eagerImage ? 'eager' : 'lazy'}
                    decoding="async"
                    onLoad={(e) => {
                      const el = e.currentTarget
                      if (!el.naturalWidth || !el.naturalHeight) {
                        setRecoveringOverlay(true)
                        handleImgFailureRef.current()
                        return
                      }
                      decodeOkRef.current = true
                      setRecoveringOverlay(false)
                      setImageLoaded(true)
                    }}
                    onError={() => {
                      setRecoveringOverlay(true)
                      handleImgFailureRef.current()
                    }}
                    className={[
                      'absolute inset-0 h-full w-full overflow-hidden object-cover',
                      angular ? 'rounded-none' : 'rounded-theme',
                      imageLoaded && !recoveringOverlay ? 'opacity-100' : 'opacity-0',
                      'transition-opacity duration-300',
                      '[@media(hover:hover)_and_(pointer:fine)]:group-hover:brightness-[1.04] dark:[@media(hover:hover)_and_(pointer:fine)]:group-hover:brightness-[1.08]',
                    ].join(' ')}
                  />
                </>
              )}
            </div>
          </button>
          {canShowCardOverlays ? (
            <ChromeToolbarButton
              type="button"
              className={[
                'absolute right-2 top-2 z-[6] !m-0 !h-7 !min-h-7 !w-7 !min-w-7 !gap-0 !p-0',
                /* 叠在缩略图上：半透明 + 轻模糊，与实心工具栏区分 */
                '!border-line/45 !bg-[color-mix(in_srgb,var(--panel)_40%,transparent)] shadow-sm backdrop-blur-[2px]',
                'hover:!border-line/60 hover:!bg-[color-mix(in_srgb,var(--panel)_58%,transparent)]',
                'opacity-[0.82] transition-[opacity,background-color,border-color] motion-reduce:transition-none hover:opacity-100',
                angular ? '!rounded-none !border-2' : '!rounded-full',
                'max-lg:opacity-100 lg:pointer-fine:opacity-0 lg:pointer-fine:group-hover:opacity-100 lg:pointer-fine:group-focus-within:opacity-100 lg:pointer-coarse:opacity-100',
              ].join(' ')}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onSelect(img)
                onOpenLightbox(img)
              }}
              aria-label={`全屏预览 ${img.name}`}
            >
              <IconEyeSmall
                className={['h-[15px] w-[15px] shrink-0', previewEyeIconClass].filter(Boolean).join(' ')}
                aria-hidden
              />
            </ChromeToolbarButton>
          ) : null}
        </div>
      )}
    </article>
  )
}

/** 列表项：首版为 ThemeStaggerItem + 栅格/分列间距由父级 gap 负责 */
function LibraryAssetGridItem({
  img,
  angular,
  selectedPublicId,
  listIndex,
  compact,
  pcDesktopColumns,
  scrollRootRef,
  scrollRootAttachSignal,
  staggerItemClassName,
  lightboxOccludedPublicId,
  onSelect,
  onOpenLightbox,
  onRefreshImageUrl,
  onFatalAssetLoadFailure,
}: {
  img: ReferenceImageItem
  angular: boolean
  selectedPublicId: string | null
  listIndex: number
  compact: boolean
  /** 桌面 lg+ 瀑布列数，传给卡片用于 `sizes` */
  pcDesktopColumns?: 1 | 2 | 3
  scrollRootRef?: RefObject<HTMLElement | null>
  scrollRootAttachSignal?: number
  /** 父级为 grid / flex 列时由 gap 控距，一般传 `min-w-0` 即可 */
  staggerItemClassName: string
  /** 灯箱预览中的条目：暂停网格图片请求，避免与灯箱重复拉同一外链 */
  lightboxOccludedPublicId?: string | null
  onSelect: (img: ReferenceImageItem) => void
  onOpenLightbox: (img: ReferenceImageItem) => void
  onRefreshImageUrl: (img: ReferenceImageItem) => Promise<ReferenceImageItem | null>
  onFatalAssetLoadFailure?: (publicId: string) => void
}) {
  const suppressImgNetwork =
    lightboxOccludedPublicId != null && lightboxOccludedPublicId === img.publicId
  const eagerImage = compact
    ? listIndex < 12
    : pcDesktopColumns === 1
      ? listIndex < 20
      : listIndex < 6
  return (
    <div className={staggerItemClassName}>
      <LibraryAssetCard
        img={img}
        angular={angular}
        active={selectedPublicId === img.publicId}
        compact={compact}
        pcDesktopColumns={compact ? undefined : pcDesktopColumns}
        eagerImage={eagerImage}
        scrollRootRef={scrollRootRef}
        scrollRootAttachSignal={scrollRootAttachSignal}
        suppressImgNetwork={suppressImgNetwork}
        onSelect={onSelect}
        onOpenLightbox={onOpenLightbox}
        onRefreshImageUrl={onRefreshImageUrl}
        onFatalLoadFailure={onFatalAssetLoadFailure}
      />
    </div>
  )
}

export function LibraryAssetsPane({
  theme,
  filtered,
  angular,
  selectedPublicId,
  scrollRootRef,
  scrollRootAttachSignal = 0,
  managementPanelOpen = true,
  lightboxOccludedPublicId,
  onSelect,
  onOpenLightbox,
  onRefreshImageUrl,
  onFatalAssetLoadFailure,
}: {
  theme: ThemeId
  filtered: ReferenceImageItem[]
  angular: boolean
  selectedPublicId: string | null
  /** 瀑布流嵌套滚动容器 ref，用于图片延后解码 / lazy root */
  scrollRootRef?: RefObject<HTMLElement | null>
  /** scroll port 挂载后递增，重建 IntersectionObserver */
  scrollRootAttachSignal?: number
  /** 桌面 lg+：右侧管理栏展开时主列较窄（列数较少）；收起后主列变宽，自动多一列瀑布 */
  managementPanelOpen?: boolean
  /** 当前灯箱对应的参考图 `publicId`，用于暂停该卡片并发请求 */
  lightboxOccludedPublicId?: string | null
  onSelect: (img: ReferenceImageItem) => void
  onOpenLightbox: (img: ReferenceImageItem) => void
  onRefreshImageUrl: (img: ReferenceImageItem) => Promise<ReferenceImageItem | null>
  onFatalAssetLoadFailure?: (publicId: string) => void
}) {
  const compact = useMediaQuery('(max-width: 1023.98px)')
  const smUp = useMediaQuery('(min-width: 640px)')
  const mainColumnWidthPx = useScrollContainerWidth(scrollRootRef, scrollRootAttachSignal)
  const layoutCompact = libraryMasonryLayoutCompact(compact)
  const masonryCols = libraryMasonryColumnCount({
    compact,
    smUp,
    managementPanelOpen,
    mainColumnWidthPx,
  })
  const masonryColsPair = masonryCols as 1 | 2 | 3
  const colGapPx = layoutCompact ? LIBRARY_MASONRY_GAP_MOBILE_PX : LIBRARY_MASONRY_GAP_DESKTOP_PX
  const gridGapClass = layoutCompact ? 'gap-3 sm:gap-4' : 'gap-5'
  const columnStackGapClass = layoutCompact ? 'gap-3' : 'gap-4'

  if (filtered.length === 0) {
    return <ThemedLibraryState theme={theme} kind="empty" />
  }

  const masonryColumns = splitLibraryMasonryColumns(filtered, masonryCols, colGapPx, layoutCompact)

  return (
    <div
      key={`wf-${masonryCols}-${layoutCompact ? 'c' : 'w'}-${managementPanelOpen ? 'mgmt' : 'wide'}`}
      className={[
        'min-w-0 grid',
        gridGapClass,
        !layoutCompact && masonryCols === 1 ? 'mx-auto max-w-3xl' : '',
      ].join(' ')}
      style={masonryGridColumnsStyle(masonryCols)}
    >
      {masonryColumns.map((col, colIdx) => (
        <div
          key={`masonry-col-${colIdx}`}
          className={['flex w-full min-w-0 flex-col', columnStackGapClass].join(' ')}
        >
          {col.map(({ img, listIndex }) => (
            <LibraryAssetGridItem
              key={img.publicId}
              img={img}
              angular={angular}
              selectedPublicId={selectedPublicId}
              listIndex={listIndex}
              compact={layoutCompact}
              pcDesktopColumns={layoutCompact ? undefined : masonryColsPair}
              scrollRootRef={scrollRootRef}
              scrollRootAttachSignal={scrollRootAttachSignal}
              staggerItemClassName="w-full min-w-0 self-stretch"
              lightboxOccludedPublicId={lightboxOccludedPublicId}
              onSelect={onSelect}
              onOpenLightbox={onOpenLightbox}
              onRefreshImageUrl={onRefreshImageUrl}
              onFatalAssetLoadFailure={onFatalAssetLoadFailure}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/** 缩略图旁或叠在图上的图标按钮：半透明，悬停再略实 */
function imageAdjacentIconButtonClass(angular: boolean) {
  return [
    chromeIconButtonClass(angular),
    'border-line/50 bg-[color-mix(in_srgb,var(--panel)_58%,transparent)] backdrop-blur-[2px]',
    'opacity-[0.88] hover:opacity-100',
  ].join(' ')
}

/** 移动顶栏：与 {@link chromeIconButtonClass} / 文件夹抽屉图标一致，避免与底部 Chrome 表样式脱节 */
function mobileLibraryChromeIconBtn(angular: boolean) {
  return [chromeIconButtonClass(angular), '!size-10 !min-h-10 !min-w-10 shrink-0 lg:hidden'].join(' ')
}

const mobileIconToolbarDangerBtn =
  'h-10 w-10 shrink-0 !justify-center !gap-0 !p-0 sm:h-10 sm:w-10 lg:hidden'

/** 桌面：右侧管理栏开/关。仅单色描边（currentColor），无填充语义色 */
function IconLibraryPanelToggle({
  open,
  angular,
  className,
  ...props
}: SVGProps<SVGSVGElement> & { open: boolean; angular?: boolean }) {
  const a = !!angular
  const sw = a ? 2.1 : 1.75
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap={a ? 'square' : 'round'}
      strokeLinejoin={a ? 'miter' : 'round'}
      className={className}
      aria-hidden
      {...props}
    >
      {open ? (
        <>
          <rect x="4" y="5.5" width="11" height="13" rx="1.25" fill="none" />
          <rect x="16" y="5.5" width="4" height="13" rx="1" fill="none" />
          <path d="M19.25 9.25 21.5 12 19.25 14.75" fill="none" />
        </>
      ) : (
        <>
          <rect x="4" y="5.5" width="7.5" height="13" rx="1.25" fill="none" />
          <rect x="12.75" y="5.5" width="7.25" height="13" rx="1.25" fill="none" />
        </>
      )}
    </svg>
  )
}

export function LibraryManagementToolbar({
  angular,
  filteredCount,
  selectedFolderPath,
  selectedItem,
  managementPanelOpen = true,
  onToggleManagementPanel,
  onNavigateFolder,
  onOpenFolderDrawer,
  onOpenDeleteFolder,
  onOpenRenameFolder,
  onOpenRename,
  onOpenMove,
  onOpenDeleteImage,
  onOpenPreview,
  uploadSlot,
  deleteFolderDisabled,
  renameFolderDisabled,
  writeDisabled,
}: {
  angular: boolean
  filteredCount: number
  selectedFolderPath: string
  selectedItem: ReferenceImageItem | null
  /** PC：右侧「管理」栏是否展开（与 onToggleManagementPanel 成对使用） */
  managementPanelOpen?: boolean
  /** PC：切换右侧管理栏显隐 */
  onToggleManagementPanel?: () => void
  onNavigateFolder: (path: string) => void
  onOpenFolderDrawer: () => void
  /** 移动端侧栏隐藏时，由此打开删除确认 */
  onOpenDeleteFolder: () => void
  /** 重命名当前所在文件夹（末段名称） */
  onOpenRenameFolder: () => void
  onOpenRename: () => void
  onOpenMove: () => void
  onOpenDeleteImage: () => void
  /** 全屏预览：移动底栏与桌面管理栏内的图标入口（顶栏不设预览按钮） */
  onOpenPreview?: () => void
  /** 图库工具栏：上传到当前目录（多选文件 / 选文件夹等） */
  uploadSlot?: ReactNode
  /** 选中系统「上传的参考图」根目录等时禁止删除 */
  deleteFolderDisabled?: boolean
  /** 图库根或本地参考图子树等不可重命名 */
  renameFolderDisabled?: boolean
  /** 无 creation:task:submit 时禁止写操作 */
  writeDisabled?: boolean
}) {
  const { theme } = useTheme()
  const previewEyeIconClass = libraryPreviewEyeIconClass(theme)
  const folderRenameBlocked = !!renameFolderDisabled || !!writeDisabled
  const folderDeleteBlocked = !!deleteFolderDisabled || !!writeDisabled
  const imageWriteBlocked = !!writeDisabled
  const [mobileImageSheetOpen, setMobileImageSheetOpen] = useState(false)

  useEffect(() => {
    if (!selectedItem) setMobileImageSheetOpen(false)
  }, [selectedItem])

  useEffect(() => {
    if (!mobileImageSheetOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileImageSheetOpen])

  useEffect(() => {
    if (!mobileImageSheetOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileImageSheetOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileImageSheetOpen])

  const closeImageSheet = useCallback(() => setMobileImageSheetOpen(false), [])

  const prefersReducedMotionToolbar = useReducedMotion()
  const mobileSheetMotionPreset = useThemeMotion()
  const mobileSheetPreset = mergeReducedMotionPreset(mobileSheetMotionPreset, !!prefersReducedMotionToolbar)
  const mobileSheetOverlayDur = Math.min(0.32, Math.max(0.2, mobileSheetPreset.duration * 0.55))
  const mobileSheetPanelDur = Math.min(0.42, Math.max(0.24, mobileSheetPreset.expandDuration * 0.72))
  const mobileSheetEase = [...mobileSheetPreset.easing] as [number, number, number, number]
  const mobileSheetReduced = !!prefersReducedMotionToolbar
  const mobileSheetBackdropTransition = mobileSheetReduced
    ? { duration: 0.16, ease: [0.4, 0, 1, 1] as [number, number, number, number] }
    : { duration: Math.min(0.24, mobileSheetOverlayDur), ease: mobileSheetEase }
  const mobileSheetPanelTransition = mobileSheetReduced
    ? { duration: 0.18, ease: [0.4, 0, 1, 1] as [number, number, number, number] }
    : { duration: mobileSheetPanelDur, ease: mobileSheetEase }
  const mobileSheetPanelHidden = mobileSheetReduced ? { opacity: 0 } : { opacity: 0, y: '100%' }
  const mobileSheetPanelVisible = { opacity: 1, y: 0 }

  const mobileToolbarActions = (
    <>
      <ChromeTooltip content="浏览与切换文件夹，可新建文件夹" angular={angular} side="bottom" align="end">
        <button
          type="button"
          onClick={() => {
            closeImageSheet()
            onOpenFolderDrawer()
          }}
          className={mobileLibraryChromeIconBtn(angular)}
          aria-label="浏览文件夹"
        >
          <IconLibraryFolder className="h-4 w-4 shrink-0" aria-hidden />
          <span className="sr-only">浏览文件夹</span>
        </button>
      </ChromeTooltip>
      {selectedFolderPath && !folderRenameBlocked ? (
        <ChromeTooltip content="重命名当前文件夹" angular={angular} side="bottom" align="end">
          <button
            type="button"
            onClick={() => {
              closeImageSheet()
              onOpenRenameFolder()
            }}
            className={mobileLibraryChromeIconBtn(angular)}
            aria-label="重命名当前文件夹"
          >
            <IconPencil className="h-4 w-4 shrink-0" aria-hidden />
            <span className="sr-only">重命名当前文件夹</span>
          </button>
        </ChromeTooltip>
      ) : null}
      {selectedFolderPath ? (
        <ChromeTooltip
          content={
            folderDeleteBlocked
              ? writeDisabled
                ? '没有图库写入权限'
                : '系统参考图目录不可删除'
              : '删除当前文件夹（须为空文件夹）'
          }
          angular={angular}
          side="bottom"
          align="end"
        >
          <ChromeToolbarDangerButton
            type="button"
            disabled={folderDeleteBlocked}
            onClick={() => {
              closeImageSheet()
              onOpenDeleteFolder()
            }}
            className={mobileIconToolbarDangerBtn}
            aria-label="删除当前文件夹"
          >
            <IconFolderTrash className="h-4 w-4 shrink-0" aria-hidden />
            <span className="sr-only">删除当前文件夹</span>
          </ChromeToolbarDangerButton>
        </ChromeTooltip>
      ) : null}
      {selectedItem && !imageWriteBlocked ? (
        <ChromeTooltip content="重命名、移动或删除所选图片" angular={angular} side="bottom" align="end">
          <button
            type="button"
            onClick={() => setMobileImageSheetOpen(true)}
            className={mobileLibraryChromeIconBtn(angular)}
            aria-label="所选图片的操作"
            aria-expanded={mobileImageSheetOpen}
          >
            <IconLibraryManage className="h-4 w-4 shrink-0" aria-hidden />
            <span className="sr-only">图片操作</span>
          </button>
        </ChromeTooltip>
      ) : null}
    </>
  )

  return (
    <>
      <div className="border-b border-line px-4 py-2.5 sm:px-6 lg:px-8 xl:px-10">
        {/* 移动顶栏：仅张数 + 操作；路径见左侧文件夹树 / 桌面第二行 / 管理栏 */}
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 sm:gap-3 lg:hidden">
          <p className="typo-lib-summary min-w-0 shrink-0 text-sm text-ink">
            共 <span className="font-semibold tabular-nums text-ink">{filteredCount}</span> 张
          </p>
          <div className="scroll-x-chipstrip flex max-w-[min(100%,70%)] shrink-0 items-center justify-end gap-1.5 overflow-x-auto overflow-y-hidden">
            {uploadSlot ? (
              <div className="flex shrink-0 items-center gap-1.5">{uploadSlot}</div>
            ) : null}
            {mobileToolbarActions}
          </div>
        </div>

        <div className="hidden lg:flex lg:flex-row lg:items-center lg:justify-between lg:gap-3">
          <div className="typo-lib-summary min-w-0 flex-1 space-y-0.5">
            <p className="min-w-0 text-sm text-ink">
              共 <span className="font-semibold tabular-nums text-ink">{filteredCount}</span> 张
            </p>
            <LibraryFolderPathBreadcrumb
              selectedFolderPath={selectedFolderPath}
              onNavigate={onNavigateFolder}
              dense
            />
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {onToggleManagementPanel ? (
              <ChromeTooltip
                content={managementPanelOpen ? '收起右侧管理栏，放大图片区域' : '展开管理栏'}
                angular={angular}
                side="bottom"
                align="end"
              >
                <button
                  type="button"
                  onClick={onToggleManagementPanel}
                  className={[chromeIconButtonClass(angular), 'inline-flex'].join(' ')}
                  aria-expanded={managementPanelOpen}
                  aria-label={managementPanelOpen ? '收起管理栏' : '展开管理栏'}
                >
                  <IconLibraryPanelToggle
                    open={managementPanelOpen}
                    angular={angular}
                    className="h-5 w-5 shrink-0"
                  />
                </button>
              </ChromeTooltip>
            ) : null}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileImageSheetOpen && selectedItem ? (
          <div
            key="library-mobile-image-sheet"
            className="fixed inset-0 z-[165] flex flex-col justify-end lg:hidden"
            role="presentation"
          >
            <motion.button
              type="button"
              aria-label="关闭"
              className="theme-modal-backdrop absolute inset-0 cursor-default"
              initial={{ opacity: 0.88 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={mobileSheetBackdropTransition}
              style={
                mobileSheetReduced
                  ? { backdropFilter: 'none', WebkitBackdropFilter: 'none' }
                  : undefined
              }
              onClick={closeImageSheet}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="library-mobile-image-sheet-title"
              className={[
                'relative z-[1] max-h-[min(88vh,36rem)] border-t border-line bg-panel px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_32px_color-mix(in_srgb,var(--ink)_28%,transparent)]',
                angular ? 'rounded-none' : 'rounded-t-2xl',
              ].join(' ')}
              style={{ transformOrigin: 'bottom center' }}
              initial={mobileSheetPanelHidden}
              animate={mobileSheetPanelVisible}
              exit={mobileSheetPanelHidden}
              transition={mobileSheetPanelTransition}
            >
              <div className="flex items-start justify-between gap-2 border-b border-line pb-3">
                <div className="min-w-0 flex-1">
                  <p id="library-mobile-image-sheet-title" className="text-sm font-semibold text-ink">
                    当前图片
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs text-ink-soft" title={selectedItem.name}>
                    {selectedItem.name.trim() || '未命名'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeImageSheet}
                  className={chromeIconButtonClass(angular)}
                  aria-label="关闭"
                >
                  <IconCloseSmall className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="mt-4 flex min-h-0 items-start gap-3">
                <div
                  className={[
                    'relative flex min-h-[4.5rem] min-w-0 flex-[0_1_46%] max-w-[min(46%,12rem)] items-center justify-center overflow-hidden bg-surface-muted ring-1 ring-line',
                    angular ? 'rounded-none' : 'rounded-xl',
                  ].join(' ')}
                >
                  <img
                    src={referenceImageThumbnailUrl(
                      resolveReferenceImageDisplayUrl(selectedItem.url, {
                        bucket: selectedItem.bucket,
                        objectKey: selectedItem.objectKey,
                      }),
                      480,
                      { bucket: selectedItem.bucket, objectKey: selectedItem.objectKey },
                    )}
                    alt=""
                    className="max-h-[min(38vh,15rem)] w-full max-w-full object-contain"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap content-start justify-start gap-2">
                  {onOpenPreview ? (
                    <ChromeTooltip content="全屏预览" angular={angular} side="top" align="start">
                      <button
                        type="button"
                        className={imageAdjacentIconButtonClass(angular)}
                        onClick={() => {
                          closeImageSheet()
                          onOpenPreview()
                        }}
                        aria-label="全屏预览"
                      >
                        <IconEyeSmall
                          className={['h-4 w-4', previewEyeIconClass].filter(Boolean).join(' ')}
                          aria-hidden
                        />
                      </button>
                    </ChromeTooltip>
                  ) : null}
                  {!imageWriteBlocked ? (
                    <ChromeTooltip content="重命名" angular={angular} side="top" align="start">
                      <button
                        type="button"
                        className={imageAdjacentIconButtonClass(angular)}
                        onClick={() => {
                          closeImageSheet()
                          onOpenRename()
                        }}
                        aria-label="重命名"
                      >
                        <IconPencil className="h-4 w-4" aria-hidden />
                      </button>
                    </ChromeTooltip>
                  ) : null}
                  {!imageWriteBlocked ? (
                    <ChromeTooltip content="移动到文件夹" angular={angular} side="top" align="start">
                      <button
                        type="button"
                        className={imageAdjacentIconButtonClass(angular)}
                        onClick={() => {
                          closeImageSheet()
                          onOpenMove()
                        }}
                        aria-label="移动到文件夹"
                      >
                        <IconMoveToFolder className="h-4 w-4" aria-hidden />
                      </button>
                    </ChromeTooltip>
                  ) : null}
                  {!imageWriteBlocked ? (
                    <ChromeTooltip content="删除图片" angular={angular} side="top" align="start">
                      <button
                        type="button"
                        className={[imageAdjacentIconButtonClass(angular), 'text-[var(--color-error)]'].join(' ')}
                        onClick={() => {
                          closeImageSheet()
                          onOpenDeleteImage()
                        }}
                        aria-label="删除图片"
                      >
                        <IconTrash className="h-4 w-4" aria-hidden />
                      </button>
                    </ChromeTooltip>
                  ) : null}
                </div>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>
    </>
  )
}

/** 侧栏标题 + 新建文件夹（与常见文件管理树一致：表单不铺在页面上） */
export function LibraryFolderSidebarToolbar({
  angular,
  onNewFolder,
  newFolderDisabled,
  writeDisabled,
}: {
  angular: boolean
  onNewFolder: () => void
  /** 选中本机参考图目录或其子路径时禁止在下方再建文件夹 */
  newFolderDisabled?: boolean
  writeDisabled?: boolean
}) {
  const createBlocked = !!newFolderDisabled || !!writeDisabled
  return (
    <div
      className={[
        'flex shrink-0 items-center justify-between gap-2 px-3 py-3 sm:px-3 sm:py-3',
        angular ? 'border-b-2 border-[var(--shell-nav-border)]' : 'border-b border-[var(--shell-rail-border)]',
      ].join(' ')}
    >
      <p className="typo-lib-section">文件夹</p>
      <ChromeTooltip
        content={
          createBlocked
            ? writeDisabled
              ? '没有图库写入权限'
              : '本地参考图目录下不可新建子文件夹'
            : '新建文件夹'
        }
        angular={angular}
        side="bottom"
        align="end"
      >
        <ChromeToolbarButton
          type="button"
          className="h-9 shrink-0 px-2.5"
          aria-label="新建文件夹"
          disabled={createBlocked}
          onClick={onNewFolder}
        >
          <IconFolderPlus className="h-4 w-4" />
        </ChromeToolbarButton>
      </ChromeTooltip>
    </div>
  )
}

export function LibraryManagementPanel({
  open,
  angular,
  selectedItem,
  selectedFolderPath,
  onNavigateFolder,
  onOpenRename,
  onOpenMove,
  onOpenDeleteImage,
  onOpenDeleteFolder,
  onOpenRenameFolder,
  onOpenPreview,
  onSyncCloud,
  syncCloudDisabled,
  deleteFolderDisabled,
  renameFolderDisabled,
  writeDisabled,
  onUpload,
  uploadDisabled,
  uploadLoading,
  onMoveFolder,
  moveFolderDisabled,
}: {
  /** 桌面 lg+：侧栏轨道收窄/展开以让出主列宽；Grid 瀑布对宽度插值较 multicol 更稳，可保留收展过渡 */
  open: boolean
  angular: boolean
  selectedItem: ReferenceImageItem | null
  selectedFolderPath: string
  onNavigateFolder: (path: string) => void
  onOpenRename: () => void
  onOpenMove: () => void
  onOpenDeleteImage: () => void
  onOpenDeleteFolder: () => void
  onOpenRenameFolder: () => void
  onOpenPreview?: () => void
  /** 桌面 lg+：同步云端图库（顶栏仅保留移动端入口） */
  onSyncCloud?: () => void
  syncCloudDisabled?: boolean
  deleteFolderDisabled?: boolean
  renameFolderDisabled?: boolean
  writeDisabled?: boolean
  /** 桌面侧栏：上传图片到当前目录 */
  onUpload?: () => void
  uploadDisabled?: boolean
  uploadLoading?: boolean
  /** 移动当前文件夹（后端已支持，按钮不再置灰） */
  onMoveFolder?: () => void
  /** 当前文件夹不可移动时置灰（系统目录 / 根目录） */
  moveFolderDisabled?: boolean
}) {
  const { theme } = useTheme()
  const previewEyeIconClass = libraryPreviewEyeIconClass(theme)
  const folderRenameBlocked = !!renameFolderDisabled || !!writeDisabled
  const folderDeleteBlocked = !!deleteFolderDisabled || !!writeDisabled
  const folderMoveBlocked = !!moveFolderDisabled || !!writeDisabled
  const imageWriteBlocked = !!writeDisabled
  const prefersReduced = useReducedMotion()
  const motionPreset = useThemeMotion()
  const preset = mergeReducedMotionPreset(motionPreset, !!prefersReduced)
  const reduced = !!prefersReduced

  /** 外层 width 与内层 opacity：与原版 motion 语义一致（减弱动效时用更短的 width、略长的淡出） */
  let shellTransition: string
  let panelOpacityTransition: string
  if (reduced) {
    const ew = Math.max(preset.expandDuration * 0.2, 0.1)
    const eo = Math.max(preset.expandDuration * 0.42, 0.16)
    const ease = 'cubic-bezier(0.4, 0, 1, 1)'
    shellTransition = `width ${ew}s ${ease}`
    panelOpacityTransition = `opacity ${eo}s ${ease}`
  } else {
    const expand = resolveExpandTransition(preset, { reducedMotion: false })
    shellTransition = cssTransitionFromExpandTransition(expand, 'width')
    panelOpacityTransition = cssTransitionFromExpandTransition(expand, 'opacity')
  }

  return (
    <aside
      aria-hidden={!open}
      className={[
        'hidden h-full min-h-0 shrink-0 overflow-hidden lg:block',
        open ? '' : 'pointer-events-none',
      ].join(' ')}
      style={{
        width: open ? LIBRARY_MANAGEMENT_RAIL_WIDTH_CSS : 0,
        minWidth: 0,
        transition: shellTransition,
      }}
    >
      <div
        className={[
          'flex h-full min-h-0 w-72 shrink-0 flex-col border-line bg-panel/70',
          angular ? 'border-l-2' : 'border-l',
        ].join(' ')}
        style={{
          opacity: open ? 1 : 0,
          transition: panelOpacityTransition,
        }}
      >
        <div className="border-b border-line px-4 py-3">
          <p className="typo-lib-section">管理</p>
          <div className="mt-1.5 flex min-w-0 items-center gap-2">
            <div className="min-w-0 flex-1">
              <LibraryFolderPathBreadcrumb selectedFolderPath={selectedFolderPath} onNavigate={onNavigateFolder} />
            </div>
            {onSyncCloud ? (
              <ChromePrimaryButton
                type="button"
                density="compact"
                className="h-9 shrink-0 px-3.5"
                onClick={onSyncCloud}
                disabled={!!syncCloudDisabled || !!writeDisabled}
              >
                同步云端图库
              </ChromePrimaryButton>
            ) : null}
          </div>
        </div>
        <div className="scroll-region min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
          <section className="space-y-2">
            <p className="text-xs font-semibold text-ink">文件夹</p>
            <div className="flex flex-wrap gap-2">
              <ChromeTooltip
                content={
                  folderDeleteBlocked
                    ? writeDisabled
                      ? '没有图库写入权限'
                      : '系统参考图目录不可删除'
                    : '删除当前文件夹（须为空文件夹）'
                }
                angular={angular}
                side="bottom"
                align="start"
              >
                <ChromeToolbarDangerButton
                  disabled={!selectedFolderPath || folderDeleteBlocked}
                  onClick={onOpenDeleteFolder}
                  className="h-10 w-10 shrink-0 justify-center gap-0 p-0 disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="删除当前文件夹"
                >
                  <IconFolderTrash className="h-4 w-4" aria-hidden />
                </ChromeToolbarDangerButton>
              </ChromeTooltip>
              <ChromeTooltip
                content={
                  folderRenameBlocked
                    ? writeDisabled
                      ? '没有图库写入权限'
                      : '该目录不可重命名'
                    : '重命名当前文件夹'
                }
                angular={angular}
                side="bottom"
                align="start"
              >
                <ChromeToolbarButton
                  type="button"
                  disabled={folderRenameBlocked}
                  onClick={onOpenRenameFolder}
                  className="h-10 w-10 shrink-0 justify-center gap-0 p-0 disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="重命名当前文件夹"
                >
                  <IconPencil className="h-4 w-4" aria-hidden />
                </ChromeToolbarButton>
              </ChromeTooltip>
              <ChromeTooltip
                content={
                  uploadDisabled
                    ? writeDisabled
                      ? '没有图库写入权限'
                      : '系统参考图目录不可上传'
                    : uploadLoading
                      ? '上传中…'
                      : '上传图片到当前目录'
                }
                angular={angular}
                side="bottom"
                align="start"
              >
                <ChromeToolbarButton
                  type="button"
                  disabled={!!uploadDisabled || !!uploadLoading}
                  onClick={onUpload}
                  className="h-10 w-10 shrink-0 justify-center gap-0 p-0 disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="上传图片到当前目录"
                >
                  <IconUploadSmall className="h-4 w-4" aria-hidden />
                </ChromeToolbarButton>
              </ChromeTooltip>
              <ChromeTooltip
                content={folderMoveBlocked ? (writeDisabled ? '没有图库写入权限' : '系统目录或根目录不可移动') : '移动文件夹到新父目录'}
                angular={angular}
                side="bottom"
                align="start"
              >
                <ChromeToolbarButton
                  type="button"
                  disabled={folderMoveBlocked}
                  onClick={onMoveFolder}
                  className="h-10 w-10 shrink-0 justify-center gap-0 p-0 disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="移动文件夹"
                >
                  <IconMoveToFolder className="h-4 w-4" aria-hidden />
                </ChromeToolbarButton>
              </ChromeTooltip>
            </div>
          </section>

          <section className="space-y-2 border-t border-line pt-4">
            <p className="text-xs font-semibold text-ink">当前图片</p>
            {selectedItem ? (
              <div
                className={['space-y-3 border border-line bg-surface-muted/35 p-3', angular ? 'rounded-none border-2' : 'rounded-xl'].join(
                  ' ',
                )}
              >
                <div
                  className={[
                    'flex min-h-0 min-w-0 items-center justify-center overflow-hidden bg-ink/[0.03] ring-1 ring-line',
                    angular ? 'rounded-none' : 'rounded-lg',
                  ].join(' ')}
                >
                  <img
                    src={referenceImageThumbnailUrl(
                      resolveReferenceImageDisplayUrl(selectedItem.url, {
                        bucket: selectedItem.bucket,
                        objectKey: selectedItem.objectKey,
                      }),
                      560,
                      { bucket: selectedItem.bucket, objectKey: selectedItem.objectKey },
                    )}
                    alt=""
                    className="max-h-[min(42vh,16rem)] w-full max-w-full object-contain"
                  />
                </div>
                <p className="truncate text-sm font-medium text-ink" title={selectedItem.name}>
                  {ellipsisMiddle(selectedItem.name, 14, 14)}
                </p>
                <div className="flex flex-wrap justify-start gap-2">
                  {onOpenPreview ? (
                    <ChromeTooltip content="全屏预览" angular={angular} side="bottom" align="start">
                      <button
                        type="button"
                        onClick={onOpenPreview}
                        className={imageAdjacentIconButtonClass(angular)}
                        aria-label="全屏预览"
                      >
                        <IconEyeSmall
                          className={['h-4 w-4', previewEyeIconClass].filter(Boolean).join(' ')}
                          aria-hidden
                        />
                      </button>
                    </ChromeTooltip>
                  ) : null}
                  {!imageWriteBlocked ? (
                    <ChromeTooltip content="重命名" angular={angular} side="bottom" align="start">
                      <button
                        type="button"
                        onClick={onOpenRename}
                        className={imageAdjacentIconButtonClass(angular)}
                        aria-label="重命名"
                      >
                        <IconPencil className="h-4 w-4" aria-hidden />
                      </button>
                    </ChromeTooltip>
                  ) : null}
                  {!imageWriteBlocked ? (
                    <ChromeTooltip content="移动到文件夹" angular={angular} side="bottom" align="start">
                      <button
                        type="button"
                        onClick={onOpenMove}
                        className={imageAdjacentIconButtonClass(angular)}
                        aria-label="移动到文件夹"
                      >
                        <IconMoveToFolder className="h-4 w-4" aria-hidden />
                      </button>
                    </ChromeTooltip>
                  ) : null}
                  {!imageWriteBlocked ? (
                    <ChromeTooltip content="删除图片" angular={angular} side="bottom" align="start">
                      <button
                        type="button"
                        onClick={onOpenDeleteImage}
                        className={[imageAdjacentIconButtonClass(angular), 'text-[var(--color-error)]'].join(' ')}
                        aria-label="删除图片"
                      >
                        <IconTrash className="h-4 w-4" aria-hidden />
                      </button>
                    </ChromeTooltip>
                  ) : null}
                </div>
              </div>
            ) : (
              <div
                className={[
                  'border border-dashed border-line py-10 text-center text-xs text-ink-soft',
                  angular ? 'rounded-none border-2' : 'rounded-xl',
                ].join(' ')}
              >
                未选中
              </div>
            )}
          </section>
        </div>
      </div>
    </aside>
  )
}
